/**
 * How a transaction's category is shown.
 *
 * The API resolves the category once, server-side (issue #21): the category a
 * person or rule assigned, else the source's label mapped to a FolioWorth
 * category (Plaid FOOD_AND_DRINK -> Restaurants), else "Uncategorised". The
 * raw source label arrives separately as `plaid_category` and is never shown
 * as if it were a category. These helpers keep every screen reading
 * `category_name` the same way instead of each picking its own field.
 */
export interface CategorisedRow {
  category_id?: string | null;
  category_name?: string | null;
  plaid_category?: string | null;
}

export const UNCATEGORISED = 'Uncategorised';

/** True when the row has no real category (either spelling, or nothing). */
export function isUncategorised(name: string | null | undefined): boolean {
  const n = name?.trim().toLowerCase();
  return !n || n === 'uncategorised' || n === 'uncategorized';
}

/** The category to display for a row. */
export function categoryLabel(row: CategorisedRow): string {
  return isUncategorised(row.category_name) ? UNCATEGORISED : (row.category_name as string).trim();
}

/** The resolved category, or undefined when there is none worth labelling with. */
export function knownCategory(row: CategorisedRow): string | undefined {
  return isUncategorised(row.category_name) ? undefined : (row.category_name as string).trim();
}

/**
 * Tooltip text explaining where the category came from, or undefined when it
 * was assigned and needs no explanation.
 */
export function categorySourceHint(row: CategorisedRow): string | undefined {
  const raw = row.plaid_category?.trim();
  if (row.category_id || !raw) return undefined;
  return isUncategorised(row.category_name)
    ? `Bank label ${raw} does not map to a category`
    : `Inferred from bank label ${raw}`;
}
